/** @format */

import {useState, useCallback} from 'react';
import SuperAgent from 'superagent';
import update from 'immutability-helper';
import {REGEX_DESIGN_FILENAME} from 'frontier-constants';

/**
 * Custom hook to handle replacing customer designs.
 *
 * @param {Object} props - The props object.
 * @param {string} props.orderId - The ID of the order.
 * @param {Function} props.onSave - Callback function to handle save response.
 * @param {Array} props.designsOrders - Array of design orders.
 * @returns {Object} - An object containing state and handler functions.
 */
export const useReplaceCustomerDesignsHandler = ({orderId, onSave, designsOrders}) => {
    const [savingReplacementDesigns, setSavingReplacementDesigns] = useState(false);
    const [unSavedDesigns, setUnSavedDesigns] = useState([]); // Uploaded but not saved

    const handleUploadComplete = useCallback(unSavedResult => {
        setUnSavedDesigns(prevDesigns => {
            const existingDesignIndex = prevDesigns.findIndex(({id}) => id === unSavedResult.id);
            if (existingDesignIndex !== -1) {
                // Don't allow the same design to overwrite multiple times
                unSavedResult.id = -1;
            }
            return update(prevDesigns, {$push: [unSavedResult]});
        });
    }, []);

    const handleUpdateDesignId = (customerFilestackId, newDesignId) => {
        setUnSavedDesigns(prevDesigns => {
            const unSavedDesignIndex = prevDesigns.findIndex(
                design => design.filestack_id === customerFilestackId
            );
            if (unSavedDesignIndex === -1) return prevDesigns;

            return update(prevDesigns, {
                [unSavedDesignIndex]: {id: {$set: parseInt(newDesignId, 10)}}
            });
        });
    };

    const handleRemoveDesignId = customerFilestackId => {
        setUnSavedDesigns(prevDesigns => {
            const unSavedDesignIndex = prevDesigns.findIndex(
                design => design.filestack_id === customerFilestackId
            );
            if (unSavedDesignIndex === -1) return prevDesigns;

            return update(prevDesigns, {
                [unSavedDesignIndex]: {id: {$set: -1}}
            });
        });
    };

    const handleSaveReplacementDesigns = useCallback(async () => {
        setSavingReplacementDesigns(true);
        try {
            const response = await SuperAgent.post(`/account/designs/save-all-designs/${orderId}`)
                .set('Accept', 'application/json')
                .send({
                    designs: unSavedDesigns,
                    type: 'customer',
                    // Only allow overwriting designs that are in this order
                    // Without this a user could delete a design
                    // Then upload a new design with the same designid_ prefix and overwrite the design they deleted
                    overwritableDesignIds: designsOrders
                        .filter(({is_reorder}) => !is_reorder)
                        .map(({design_id}) => design_id)
                });

            onSave(response.body);
        } catch (error) {
            console.error('Error saving replacement designs:', error);
            // Handle error appropriately (e.g., set error state, show notification)
        } finally {
            setSavingReplacementDesigns(false);
        }
    }, [orderId, unSavedDesigns, onSave, designsOrders]);

    const handleRemoveUnsavedDesigns = useCallback(designId => {
        setUnSavedDesigns(prevDesigns => {
            const designIndex = prevDesigns.findIndex(design => design.id === designId);
            if (designIndex === -1) return prevDesigns;

            return update(prevDesigns, {$splice: [[designIndex, 1]]});
        });
    }, []);

    const handleCreateFileEntity = useCallback(uploadedFile => {
        return {
            order_id: uploadedFile.orderId,
            filename: uploadedFile.filename.replace(REGEX_DESIGN_FILENAME, ''),
            key: uploadedFile.key,
            filesize: uploadedFile.size,
            mimetype: uploadedFile.mimetype,
            filestack_id: uploadedFile.handle
        };
    }, []);

    return {
        savingReplacementDesigns,
        unSavedDesigns,
        handleRemoveDesignId,
        handleRemoveUnsavedDesigns,
        handleUpdateDesignId,
        handleSaveReplacementDesigns,
        handleUploadComplete,
        handleCreateFileEntity
    };
};
